import { useCallback, useMemo, useState } from "react";
import { useBrandDetails } from "../../brand/hooks/useBrandDetails";
import type { BrandDetails } from "../../brand/types/brand.types";
import { useBillingProfile } from "./useBillingProfile";
import type { BillingDetails } from "../types/billing.types";
import {
  BillingErrors,
  ValidatedField,
  validateBillingDetails,
} from "../validation";
import { FIELD_ORDER } from "../components/BillingDetailsForm";

/** What the form opens as before anything is known about the buyer. */
const EMPTY_DETAILS: BillingDetails = {
  fullName: "",
  email: "",
  phone: "",
  gstin: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

/**
 * The saved brand, as far as it answers the questions this form asks.
 *
 * Field for field rather than a spread: the two shapes overlap by name but not
 * by meaning — a brand carries bank details and a logo, none of which is
 * anyone's billing address — and spreading one into the other would carry
 * whatever is added to it next.
 *
 * There is no GSTIN among them. Nothing in the app collects one yet, so it is
 * the single field these screens always ask for cold.
 */
const fromBrand = (brand: BrandDetails): BillingDetails => ({
  fullName: brand.fullName,
  email: brand.email,
  phone: brand.phone,
  gstin: "",
  address: brand.address,
  city: brand.city,
  state: brand.state,
  pincode: brand.pincode,
});

export interface BillingFormState {
  details: BillingDetails;
  /** Patch merge — every control sends only the key it owns. */
  change: (patch: Partial<BillingDetails>) => void;
  /** Replaces the lot: what the server answered a save with, which is what the
   *  invoice will actually carry. */
  reset: (details: BillingDetails) => void;
  /** Everything failing at once, whether or not it is being shown. */
  errors: BillingErrors;
  touched: Partial<Record<ValidatedField, boolean>>;
  visit: (field: ValidatedField) => void;
  /** Marks every field visited, so a form submitted whole shows everything
   *  wrong with it rather than the next problem one at a time. */
  showAllErrors: () => void;
  /** The first field, in layout order, that is in the way — or `null` when the
   *  form is ready to submit. */
  firstFailing: ValidatedField | null;
  /** The two records this fills itself from are still on their way. */
  loading: boolean;
  /** Neither arrived. Not fatal: the form works perfectly well typed into by
   *  hand, and the screens say why it is empty. */
  loadFailed: boolean;
  /** Records who to invoice and answers with what was stored. Throws where the
   *  write was refused, so the screen can say what the server said. */
  save: (details: BillingDetails) => Promise<BillingDetails>;
}

/**
 * Who the subscription is billed to, as a form: what it opens holding, what is
 * wrong with it, and how it is written back.
 *
 * One hook rather than the same forty lines on both screens that ask. The paid
 * checkout and the trial's detail step collect the identical eight fields for
 * the identical reason — an invoice has to be headed with something — and they
 * must agree about where those come from, when an error is allowed to show and
 * what happens on a save. A second copy is how the two would quietly drift into
 * asking the same question differently.
 *
 * ## What it opens holding
 *
 * The saved billing profile, or the brand where there is none. The profile wins
 * because it is the answer to this form's own question — who Naurist invoices —
 * while the brand is a good guess assembled from a different one: a dietitian
 * who has set their practice up has already typed most of this once and should
 * not have to again.
 *
 * Seeded during the render that first sees both loads rather than in an effect
 * after them: an effect would paint the empty form once and fill it a frame
 * later, which reads as the screen overwriting itself. React re-runs the render
 * with the new state before committing either version of it. The same pattern,
 * and the same reasoning, as `MyBrandPage`.
 *
 * Must be used inside a Clerk provider, an active session and a
 * `<ToastProvider>` — `useBrandDetails` reports its own half-failures there.
 */
export function useBillingForm(): BillingFormState {
  const billingProfile = useBillingProfile();
  const { loaded, status } = useBrandDetails();

  const [details, setDetails] = useState<BillingDetails>(EMPTY_DETAILS);
  const [touched, setTouched] = useState<
    Partial<Record<ValidatedField, boolean>>
  >({});

  // `null` until both have settled, so the profile and the brand are never
  // painted one after the other.
  const openWith: BillingDetails | null =
    billingProfile.status === "loading" || status === "loading"
      ? null
      : billingProfile.profile ?? (loaded ? fromBrand(loaded) : EMPTY_DETAILS);

  const [seeded, setSeeded] = useState(false);
  if (openWith && !seeded) {
    setSeeded(true);
    setDetails(openWith);
  }

  const errors = useMemo(() => validateBillingDetails(details), [details]);

  const change = useCallback(
    (patch: Partial<BillingDetails>) =>
      setDetails((current) => ({ ...current, ...patch })),
    []
  );

  const visit = useCallback(
    (field: ValidatedField) =>
      setTouched((current) => ({ ...current, [field]: true })),
    []
  );

  const showAllErrors = useCallback(
    () =>
      setTouched(
        FIELD_ORDER.reduce(
          (all, field) => ({ ...all, [field]: true }),
          {} as Partial<Record<ValidatedField, boolean>>
        )
      ),
    []
  );

  return {
    details,
    change,
    reset: setDetails,
    errors,
    touched,
    visit,
    showAllErrors,
    firstFailing: FIELD_ORDER.find((field) => errors[field]) ?? null,
    loading: billingProfile.status === "loading" || status === "loading",
    loadFailed: billingProfile.status === "failed" || status === "failed",
    save: billingProfile.save,
  };
}

export default useBillingForm;
